// Feed.jsx — FINAL FULL FILE
import React, { useState, useEffect, useContext } from "react";
import FeedForm from "../../components/FeedForm";
import MainLayout from "../../layout/MainLayout";

import { api } from "../../api/axios";
import { AuthContext } from "../../context/AuthContext";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";

import {
    Search,
    Pen,
    Trash2,
    Info,
    X,
    Heart,
    MessageCircle,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";

import { fileUlr } from "../../api/url";

/* -------------------------------------------------------------------------- */
/*                              MAIN FEED PAGE                                */
/* -------------------------------------------------------------------------- */

export default function Feed() {
    const { loading } = useContext(AuthContext);

    const [viewMode, setViewMode] = useState("table");

    const [feedData, setFeedData] = useState([]);
    const [feedDetails, setFeedDetails] = useState(null);

    // Filters
    const [search, setSearch] = useState("");
    const [userFilter, setUserFilter] = useState("all");

    // Pagination
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20); // default rows per page

    // Build dynamic query
    const buildQuery = () => {
        const params = new URLSearchParams();

        params.append("page", page);
        params.append("limit", limit);

        if (search.trim() !== "") params.append("search", search);
        if (userFilter !== "all") params.append("userType", userFilter);

        return params.toString();
    };

    const fetchPosts = async () => {
        try {
            const res = await api.get(`/posts?${buildQuery()}`);
            setFeedData(res.data.posts || []);
            setFeedDetails(res.data || {});
        } catch (err) {
            console.log(err);
            toast.error("Failed to load posts");
        }
    };

    // Fetch when filters or pagination changes
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            if (!loading) {
                fetchPosts();
            }
        }, 1000);

        return () => clearTimeout(delayDebounce);
    }, [loading, page, search, userFilter, limit]);

    /* ---------------------------------------------------------------------- */
    /*                              PAGE RETURN                               */
    /* ---------------------------------------------------------------------- */

    return (
        <MainLayout
            title="Feed"
            Buttons={
                <button
                    onClick={() => setViewMode(viewMode === "table" ? "form" : "table")}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md"
                >
                    {viewMode === "table" ? "Add New Post" : "Back to List"}
                </button>
            }
        >

            {/* Create Form */}
            {viewMode === "form" && (
                <div className="p-6">
                    <FeedForm
                        onSuccess={() => {
                            setPage(1);
                            fetchPosts();
                            setViewMode("table");
                        }}
                    />
                </div>
            )}

            {/* LIST TABLE */}
            {viewMode === "table" && (
                <div className="p-6">

                    {/* Filters */}
                    <FilterBar
                        search={search}
                        setSearch={setSearch}
                        userFilter={userFilter}
                        setUserFilter={setUserFilter}
                    />

                    {/* Table */}
                    <div className="mt-6">
                        <FeedTable data={feedData} refresh={fetchPosts} />
                    </div>

                    {/* Pagination */}
                    <div className="mt-6">
                        <Pagination
                            totalPages={feedDetails?.totalPages || 1}
                            page={page}
                            onChange={setPage}
                            limit={limit}
                            setLimit={setLimit}
                        />
                    </div>

                    <p className="text-gray-600 text-sm mt-3">
                        Actual Total Posts: {feedDetails?.totalPosts ?? 0} &nbsp; | &nbsp;
                        Filtered Results: {feedDetails?.matchFilter ?? feedData?.length ?? 0} &nbsp; | &nbsp;
                        Page {feedDetails?.page ?? 1} of {feedDetails?.totalPages ?? 1}
                    </p>


                </div>
            )}

        </MainLayout>
    );
}

/* -------------------------------------------------------------------------- */
/*                               FILTER BAR                                    */
/* -------------------------------------------------------------------------- */

function FilterBar({ search, setSearch, userFilter, setUserFilter }) {
    return (
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">

            {/* SEARCH */}
            <div className="relative w-full md:w-1/2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                    }}
                    placeholder="Search posts..."
                    className="w-full pl-10 pr-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-blue-500"
                />
            </div>

            {/* USER FILTER */}
            <select
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
                className="px-3 py-2 border rounded-lg bg-white"
            >
                <option value="all">All Users</option>
                <option value="users">Users</option>
                <option value="admins">Admins</option>
            </select>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*                               FEED TABLE                                   */
/* -------------------------------------------------------------------------- */

function FeedTable({ data, refresh }) {
    const [selectedPost, setSelectedPost] = useState(null);

    return (
        <>
            <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">

                    <thead className="bg-gray-50">
                        <tr>
                            {["#", "User", "Description", "Likes", "Comments", "Date", "Actions"].map((h) => (
                                <th key={h} className="px-4 py-3 text-left text-sm text-gray-700 font-medium">
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {data.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="text-center py-6 text-gray-500">
                                    No posts found
                                </td>
                            </tr>
                        ) : (
                            data.map((item, i) => (
                                <FeedRow
                                    key={item._id}
                                    item={item}
                                    index={i}
                                    setSelectedPost={setSelectedPost}
                                    refresh={refresh}
                                />
                            ))
                        )}
                    </tbody>

                </table>
            </div>

            {/* Modal */}
            {selectedPost && (
                <PostModal
                    post={selectedPost}
                    onClose={() => setSelectedPost(null)}
                />
            )}
        </>
    );
}

/* -------------------------------------------------------------------------- */
/*                               FEED ROW                                     */
/* -------------------------------------------------------------------------- */

function FeedRow({ item, index, setSelectedPost, refresh }) {
    const [processing, setProcessing] = useState(false);

    // Delete post
    async function handleDelete(id) {
        const confirm = window.confirm("Are you sure you want to delete this post?");
        if (!confirm) return;
        try {
            setProcessing(true);
            await api.delete(`/posts/${id}`);
            toast.success("Post deleted");
            refresh();
        } catch (err) {
            toast.error("Delete failed");
        } finally {
            setProcessing(false);
        }
    }

    return (
        <tr className="hover:bg-gray-50">

            <td className="px-4 py-3">{index + 1}</td>
            <td className="px-4 py-3">{item.userType === "admins" ? "Jambhanai Live App" : item.user?.name}</td>

            <td className="px-4 py-3 max-w-md">
                {item.description.length > 60
                    ? item.description.slice(0, 60) + "..."
                    : item.description}
            </td>

            <td className="px-4 py-3 text-center">{item.likes}</td>
            <td className="px-4 py-3 text-center">{item.comments.length}</td>

            <td className="px-4 py-3">{new Date(item.createdAt).toLocaleString()}</td>

            <td className="px-4 py-3">
                <div className="flex gap-3">

                    {/* Details */}
                    <button
                        onClick={() => setSelectedPost(item)}
                        className="p-2 rounded-md hover:bg-gray-100"
                    >
                        <Info size={16} className="text-blue-600" />
                    </button>

                    {/* Edit */}
                    <button className="p-2 rounded-md hover:bg-gray-100">
                        <Pen size={16} className="text-green-600" />
                    </button>

                    {/* Delete */}
                    <button
                        onClick={() => handleDelete(item._id)}
                        className="p-2 rounded-md hover:bg-gray-100"
                        disabled={processing}
                    >
                        <Trash2 size={16} className="text-red-600" />
                    </button>

                </div>
            </td>

        </tr>
    );
}

/* -------------------------------------------------------------------------- */
/*                              POST DETAILS MODAL                            */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/*                              POST DETAILS MODAL                            */
/* -------------------------------------------------------------------------- */

function PostModal({ post: initialPost, onClose }) {
    const [post, setPost] = useState(initialPost);
    const [loading, setLoading] = useState(true);
    const [index, setIndex] = useState(0);
    const [activeTab, setActiveTab] = useState("comments"); // "comments" | "likes"

    useEffect(() => {
        const fetchPostDetails = async () => {
            try {
                const res = await api.get(`/posts/${initialPost._id}`);
                if (res.status === 200) {
                    setPost(res.data);
                }
            } catch (err) {
                console.error("Failed to fetch post details", err);
                toast.error("Failed to load latest details");
            } finally {
                setLoading(false);
            }
        };

        if (initialPost._id) {
            fetchPostDetails();
        }
    }, [initialPost._id]);

    const images = post.image || [];

    const nextImage = () => {
        setIndex((prev) => (prev + 1) % images.length);
    };

    const prevImage = () => {
        setIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    // Build comment threads
    const buildThreadedComments = () => {
        const threads = [];
        const comments = post.comments || [];

        comments.forEach((c) => {
            const isReply = c.comment.startsWith("@");
            if (!isReply) {
                // Normal comment → main thread
                threads.push({ ...c, replies: [] });
            }
        });

        // Attach replies
        comments.forEach((c) => {
            const isReply = c.comment.startsWith("@");
            if (isReply) {
                const replyTarget = c.comment.split(" ")[0].substring(1); // remove "@"

                const parent = threads.find(t => t.userId?.username === replyTarget);

                if (parent) {
                    parent.replies.push(c);
                } else {
                    // If no parent found, treat as standalone
                    threads.push({ ...c, replies: [] });
                }
            }
        });

        return threads;
    };

    const threadedComments = buildThreadedComments();


    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">

            <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full relative overflow-hidden flex flex-col max-h-[90vh]">

                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-3 right-3 p-2 bg-gray-200 hover:bg-gray-300 rounded-full z-10"
                >
                    <X size={20} />
                </button>

                {/* Header */}
                <div className="p-4 border-b shrink-0">
                    <h2 className="text-lg font-semibold">{post.user?.name}</h2>
                    <p className="text-xs text-gray-500">
                        {new Date(post.createdAt).toLocaleString()}
                    </p>
                </div>

                {loading ? (
                    <div className="flex-1 flex items-center justify-center min-h-[300px]">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                    </div>
                ) : (
                    /* MAIN CONTENT */
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 overflow-y-auto">

                        {/* ============================= LEFT SIDE (IMAGES) ============================= */}
                        <div className="space-y-4">

                            {/* MAIN IMAGE */}
                            <div className="relative bg-gray-100 rounded-lg flex items-center justify-center min-h-[300px] overflow-hidden">
                                {images.length > 0 ? (
                                    <img
                                        src={`${fileUlr}/${images[index]}`}
                                        className="max-h-[350px] object-contain rounded-md"
                                        alt="Post content"
                                    />
                                ) : (
                                    <p className="text-gray-400">No Image Available</p>
                                )}

                                {/* PREV */}
                                {images.length > 1 && (
                                    <button
                                        onClick={prevImage}
                                        className="absolute left-0 top-1/2 -translate-y-1/2 px-2 py-3 bg-black/40 text-white hover:bg-black/60 rounded-r-md"
                                    >
                                        <ChevronLeft size={20} />
                                    </button>
                                )}

                                {/* NEXT */}
                                {images.length > 1 && (
                                    <button
                                        onClick={nextImage}
                                        className="absolute right-0 top-1/2 -translate-y-1/2 px-2 py-3 bg-black/40 text-white hover:bg-black/60 rounded-l-md"
                                    >
                                        <ChevronRight size={20} />
                                    </button>
                                )}
                            </div>

                            {/* THUMBNAILS */}
                            {images.length > 1 && (
                                <div className="flex gap-2 overflow-x-auto pb-2">
                                    {images.map((img, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setIndex(i)}
                                            className={`p-1 border rounded-lg ${i === index ? "border-blue-600" : "border-gray-300"
                                                }`}
                                        >
                                            <img
                                                src={`${fileUlr}/${img}`}
                                                className="h-16 w-16 object-cover rounded"
                                                alt={`Thumbnail ${i}`}
                                            />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* ============================= RIGHT SIDE (INFO) ============================= */}
                        <div className="space-y-4 flex flex-col h-full">

                            {/* Description */}
                            <p className="text-gray-800 whitespace-pre-wrap shrink-0">
                                {post.description}
                            </p>

                            {/* LIKES & COMMENTS COUNT */}
                            <div className="flex items-center gap-4 text-sm shrink-0">
                                <button
                                    onClick={() => setActiveTab("likes")}
                                    className="flex items-center gap-1 text-red-500 hover:underline"
                                >
                                    <Heart size={16} /> {post.likes} Likes
                                </button>

                                <button
                                    onClick={() => setActiveTab("comments")}
                                    className="flex items-center gap-1 text-blue-500 hover:underline"
                                >
                                    <MessageCircle size={16} /> {post.comments?.length || 0} Comments
                                </button>
                            </div>

                            {/* ============================= TAB SWITCHER ============================= */}
                            <div className="flex border-b shrink-0">
                                <button
                                    onClick={() => setActiveTab("comments")}
                                    className={`px-4 py-2 text-sm font-medium ${activeTab === "comments"
                                        ? "border-b-2 border-blue-600 text-blue-600"
                                        : "text-gray-500"
                                        }`}
                                >
                                    Comments
                                </button>

                                <button
                                    onClick={() => setActiveTab("likes")}
                                    className={`px-4 py-2 text-sm font-medium ${activeTab === "likes"
                                        ? "border-b-2 border-purple-600 text-purple-600"
                                        : "text-gray-500"
                                        }`}
                                >
                                    Likes
                                </button>
                            </div>

                            {/* ============================= COMMENTS TAB ============================= */}
                            {activeTab === "comments" && (
                                <div className="flex-1 overflow-y-auto space-y-4 pr-2 mb-2 min-h-[200px]">

                                    {threadedComments.length === 0 ? (
                                        <p className="text-gray-500 text-sm mt-4 text-center">No comments found.</p>
                                    ) : (
                                        threadedComments.map((comment) => (
                                            <div key={comment._id}>

                                                {/* MAIN COMMENT */}
                                                <div className="flex items-start gap-2 p-2 bg-gray-100 rounded-md">
                                                    <div className="h-8 w-8 bg-gray-300 rounded-full flex items-center justify-center uppercase text-sm font-bold shrink-0">
                                                        {comment.userId?.name?.[0]}
                                                    </div>

                                                    <div className="flex-1">
                                                        <p className="font-semibold text-xs">
                                                            {comment.userId?.name}{" "}
                                                            <span className="text-blue-500">@{comment.userId?.username}</span>
                                                        </p>

                                                        <p className="text-xs text-gray-800 whitespace-pre-wrap">
                                                            {comment.comment}
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* REPLIES */}
                                                {comment.replies.length > 0 && (
                                                    <div className="ml-10 mt-2 space-y-2">

                                                        {comment.replies.map((reply) => {
                                                            const replyTag = reply.comment.split(" ")[0];
                                                            const replyText = reply.comment.split(" ").slice(1).join(" ");

                                                            return (
                                                                <div
                                                                    key={reply._id}
                                                                    className="flex items-start gap-2 p-2 bg-gray-50 rounded-md border-l-2 border-blue-400"
                                                                >
                                                                    <div className="h-7 w-7 bg-gray-300 rounded-full flex items-center justify-center uppercase text-xs font-bold shrink-0">
                                                                        {reply.userId?.name?.[0]}
                                                                    </div>

                                                                    <div className="flex-1">
                                                                        <p className="font-semibold text-xs">
                                                                            {reply.userId?.name}{" "}
                                                                            <span className="text-blue-500">@{reply.userId?.username}</span>
                                                                        </p>

                                                                        <p className="text-xs text-gray-800">
                                                                            <span className="text-blue-600 font-medium">{replyTag}</span>{" "}
                                                                            {replyText}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}

                                                    </div>
                                                )}

                                            </div>
                                        ))
                                    )}

                                </div>
                            )}


                            {/* ============================= LIKES TAB ============================= */}
                            {activeTab === "likes" && (
                                <div className="flex-1 overflow-y-auto pr-2 min-h-[200px]">
                                    {post.likeUsers?.length > 0 ? (
                                        post.likeUsers.map((u) => (
                                            <div
                                                key={u._id}
                                                className="p-2 bg-gray-100 rounded-md flex items-center gap-2 mb-2"
                                            >
                                                <div className="h-8 w-8 bg-gray-300 rounded-full flex items-center justify-center uppercase shrink-0">
                                                    {u.picture ? (
                                                        <img
                                                            src={u.picture.startsWith("http") ? u.picture : `${fileUlr}/uploads/${u.picture}`}
                                                            className="h-8 w-8 rounded-full object-cover"
                                                            alt={u.name}
                                                        />
                                                    ) : (
                                                        u.name?.[0]
                                                    )}
                                                </div>
                                                <div className="flex flex-col">
                                                    <p className="text-sm font-medium">
                                                        {u.name}
                                                    </p>
                                                    <span className="text-xs text-gray-500">@{u.username}</span>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-gray-500 text-sm mt-4 text-center">No likes yet</p>
                                    )}
                                </div>
                            )}

                        </div>

                    </div>
                )}
            </div>
        </div>
    );
}


/* -------------------------------------------------------------------------- */
/*                             SMART PAGINATION                               */
/* -------------------------------------------------------------------------- */

function Pagination({ totalPages, page, onChange, limit, setLimit }) {
    if (!totalPages || totalPages <= 1) return null;

    const MAX_VISIBLE = 2;

    const pages = [];
    let start = Math.max(1, page - MAX_VISIBLE);
    let end = Math.min(totalPages, page + MAX_VISIBLE);

    if (start > 1) pages.push(1);
    if (start > 2) pages.push("dots-start");

    for (let p = start; p <= end; p++) pages.push(p);

    if (end < totalPages - 1) pages.push("dots-end");
    if (end < totalPages) pages.push(totalPages);

    return (
        <div className="flex items-center justify-between w-full">

            {/* Limit Selector */}
            <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Rows per page:</span>
                <select
                    value={limit}
                    onChange={(e) => {
                        setLimit(Number(e.target.value));
                        onChange(1); // Reset page
                    }}
                    className="px-3 py-1 border rounded-md"
                >
                    {[10, 20, 50, 100].map((n) => (
                        <option key={n} value={n}>{n}</option>
                    ))}
                </select>
            </div>

            {/* Page Buttons */}
            <div className="flex items-center gap-1">

                {/* Prev */}
                <button
                    disabled={page === 1}
                    onClick={() => onChange(page - 1)}
                    className="px-3 py-1 border rounded-md disabled:opacity-40"
                >
                    Prev
                </button>

                {pages.map((p, i) =>
                    p === "dots-start" || p === "dots-end" ? (
                        <span key={i} className="px-2">...</span>
                    ) : (
                        <button
                            key={i}
                            onClick={() => onChange(p)}
                            className={`px-3 py-1 rounded-md ${p === page
                                ? "bg-gray-900 text-white"
                                : "bg-gray-200 text-gray-700"
                                }`}
                        >
                            {p}
                        </button>
                    )
                )}

                {/* Next */}
                <button
                    disabled={page === totalPages}
                    onClick={() => onChange(page + 1)}
                    className="px-3 py-1 border rounded-md disabled:opacity-40"
                >
                    Next
                </button>

            </div>

        </div>
    );
}
